import { Controller } from '@hotwired/stimulus'
import * as d3 from 'd3'

export default class extends Controller {
  static values = { data: Array, name: String, categories: Array }

  connect() {
    d3.select(this.element).selectAll('svg').remove()
    this.drawFunnel()
  }

  drawFunnel() {
    const container = this.element.getBoundingClientRect()
    const width = Math.max(container.width || 500, 400)
    const height = Math.max(container.height || 240, 200)
    const margin = { top: 36, right: 150, bottom: 20, left: 150 }

    const svg = d3
      .select(this.element)
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')

    const defs = svg.append('defs')

    if (this.nameValue) {
      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', 22)
        .attr('text-anchor', 'middle')
        .style('font-family', 'Inter, system-ui, sans-serif')
        .style('font-size', '16px')
        .style('font-weight', '600')
        .style('fill', '#1f2937')
        .text(this.nameValue)
    }

    const graphWidth = width - margin.left - margin.right
    const graphHeight = height - margin.top - margin.bottom
    const gap = 3
    const count = this.dataValue.length
    const sectionHeight = (graphHeight - gap * Math.max(count - 1, 1)) / count
    const maxWidth = graphWidth

    // A stage's width honestly encodes its size relative to the funnel's entry
    // stage: a zero stage collapses to nothing, while a small-but-real stage keeps
    // a legible minimum sliver so it stays visible.
    const MIN_RATIO = 0.06
    const topValue = this.dataValue[0] || 1
    const widthFor = (value) => {
      if (!value || value <= 0) return 0
      return maxWidth * Math.min(Math.max(value / topValue, MIN_RATIO), 1)
    }

    const funnelData = this.dataValue.map((value, index) => {
      const nextValue = index < count - 1 ? this.dataValue[index + 1] : null
      const topWidth = widthFor(value)
      // Each band's bottom edge meets the next band's top edge, so the funnel is
      // continuous; the final band tapers gently inward.
      const bottomWidth = nextValue !== null ? widthFor(nextValue) : topWidth * 0.6

      return {
        topWidth,
        bottomWidth,
        y: margin.top + index * (sectionHeight + gap),
        value,
        category: this.categoriesValue[index],
        pct: topValue > 0 ? Math.round((value / topValue) * 100) : 0,
        gradId: `funnel-grad-${index}`,
      }
    })

    const colorStops = ['#fbbf24', '#fcd34d', '#fef3c7']
    const colorScale = d3
      .scaleLinear()
      .domain([0, Math.max(this.dataValue.length - 1, 1)])
      .range([0, colorStops.length - 1])

    const getColor = (i) => {
      const pos = colorScale(i)
      const lower = Math.floor(pos)
      const upper = Math.min(lower + 1, colorStops.length - 1)
      return d3.interpolateRgb(colorStops[lower], colorStops[upper])(pos - lower)
    }

    funnelData.forEach((d, i) => {
      const grad = defs
        .append('linearGradient')
        .attr('id', `funnel-grad-${i}`)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '0%')
        .attr('y2', '100%')
      grad.append('stop').attr('offset', '0%').attr('stop-color', getColor(i)).attr('stop-opacity', 0.9)
      grad.append('stop').attr('offset', '100%').attr('stop-color', getColor(i)).attr('stop-opacity', 0.7)
    })

    const centerX = margin.left + graphWidth / 2
    const maxRadius = 6

    // Skip bands with no width (a zero stage) — the row keeps its labels, but
    // there is no misleading block to imply volume that isn't there.
    svg
      .selectAll('.funnel-section')
      .data(funnelData.filter((d) => d.topWidth > 0.5 || d.bottomWidth > 0.5))
      .enter()
      .append('path')
      .attr('d', (d) => {
        const tl = centerX - d.topWidth / 2
        const tr = centerX + d.topWidth / 2
        const bl = centerX - d.bottomWidth / 2
        const br = centerX + d.bottomWidth / 2
        const y1 = d.y
        const y2 = d.y + sectionHeight
        const rt = Math.min(maxRadius, d.topWidth / 2)
        const rb = Math.min(maxRadius, d.bottomWidth / 2)

        return `M ${tl + rt} ${y1}
                L ${tr - rt} ${y1}
                Q ${tr} ${y1} ${tr} ${y1 + rt}
                L ${br} ${y2 - rb}
                Q ${br} ${y2} ${br - rb} ${y2}
                L ${bl + rb} ${y2}
                Q ${bl} ${y2} ${bl} ${y2 - rb}
                L ${tl} ${y1 + rt}
                Q ${tl} ${y1} ${tl + rt} ${y1} Z`
      })
      .attr('fill', (d) => `url(#${d.gradId})`)
      .style('cursor', 'default')
      .on('mouseover', function () {
        d3.select(this).transition().duration(150).style('filter', 'brightness(0.95)')
      })
      .on('mouseout', function () {
        d3.select(this).transition().duration(150).style('filter', 'none')
      })

    // Labels live in fixed left/right gutters so they read as tidy columns no
    // matter how narrow a band gets (a near-empty stage would otherwise jam its
    // labels together at the center).
    const labelLeftX = margin.left - 10
    const labelRightX = width - margin.right + 12

    svg
      .selectAll('.category-label')
      .data(funnelData)
      .enter()
      .append('text')
      .attr('x', labelLeftX)
      .attr('y', (d) => d.y + sectionHeight / 2)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'central')
      .style('font-family', 'Inter, system-ui, sans-serif')
      .style('font-size', '13px')
      .style('font-weight', '500')
      .style('fill', '#6b7280')
      .text((d) => d.category || 'N/A')

    svg
      .selectAll('.value-label')
      .data(funnelData)
      .enter()
      .append('text')
      .attr('x', labelRightX)
      .attr('y', (d) => d.y + sectionHeight / 2)
      .attr('text-anchor', 'start')
      .attr('dominant-baseline', 'central')
      .style('font-family', 'Inter, system-ui, sans-serif')
      .style('font-size', '14px')
      .style('font-weight', '700')
      .style('fill', '#1f2937')
      .text((d) => d.value.toLocaleString('pt-BR'))

    svg
      .selectAll('.pct-label')
      .data(funnelData)
      .enter()
      .append('text')
      .attr('x', labelRightX)
      .attr('y', (d) => d.y + sectionHeight / 2 + 16)
      .attr('text-anchor', 'start')
      .attr('dominant-baseline', 'central')
      .style('font-family', 'Inter, system-ui, sans-serif')
      .style('font-size', '11px')
      .style('font-weight', '400')
      .style('fill', '#9ca3af')
      .text((d, i) => (i === 0 ? '' : `${d.pct}%`))
  }

  disconnect() {
    d3.select(this.element).selectAll('svg').remove()
  }
}
